import { supabase } from '../lib/supabase';

/**
 * Submit a review for an expert after a completed contract.
 * The DB enforces that the caller must be the client on a completed contract,
 * and that they have not already reviewed it (UNIQUE constraint).
 */
export async function submitReview({ contractId, expertId, rating, title, comment }) {
  try {
    const { data: { user } } = await supabase.auth.getUser();
    if (!user) return { success: false, error: 'Not authenticated' };

    const { data, error } = await supabase
      .from('expert_reviews')
      .insert({
        contract_id: contractId,
        expert_id: expertId,
        client_id: user.id,
        rating,
        title: title?.trim() || null,
        comment: comment?.trim() || null,
      })
      .select()
      .single();

    if (error) throw error;
    return { success: true, review: data };
  } catch (error) {
    console.error('submitReview error:', error);
    return { success: false, error: error?.message };
  }
}

/**
 * Fetch all public reviews for an expert profile, newest first.
 * Returns reviewer's display name from user_profiles via the SD RPC pattern.
 */
export async function getExpertReviews(expertProfileId) {
  try {
    const { data, error } = await supabase
      .from('expert_reviews')
      .select('id, contract_id, rating, title, comment, created_at, client_id')
      .eq('expert_id', expertProfileId)
      .order('created_at', { ascending: false });

    if (error) throw error;

    // Fetch client display names using the existing get_user_basic_profiles RPC
    let reviews = data || [];
    if (reviews.length > 0) {
      const clientIds = [...new Set(reviews.map((r) => r.client_id))];
      const { data: profiles } = await supabase.rpc('get_user_basic_profiles', {
        p_user_ids: clientIds,
      });
      if (profiles) {
        const map = Object.fromEntries(profiles.map((p) => [p.id, p]));
        reviews = reviews.map((r) => ({
          ...r,
          client: map[r.client_id] || null,
        }));
      }
    }

    return { success: true, reviews };
  } catch (error) {
    console.error('getExpertReviews error:', error);
    return { success: false, reviews: [], error: error?.message };
  }
}

/**
 * Returns true if the current user has already submitted a review for this contract.
 */
export async function hasReviewedContract(contractId) {
  try {
    const { data: { user } } = await supabase.auth.getUser();
    if (!user) return false;
    const { data } = await supabase
      .from('expert_reviews')
      .select('id')
      .eq('contract_id', contractId)
      .eq('client_id', user.id)
      .maybeSingle();
    return !!data?.id;
  } catch {
    return false;
  }
}

/**
 * Returns the most-recent completed contract the current user has with an expert,
 * plus whether they've already reviewed it.
 * Returns null if no completed contract exists.
 */
export async function getClientContractWithExpert(expertProfileId) {
  try {
    const { data, error } = await supabase.rpc('get_client_contract_with_expert', {
      p_expert_id: expertProfileId,
    });

    if (error) throw error;
    return { success: true, row: data?.[0] || null };
  } catch (error) {
    console.error('getClientContractWithExpert error:', error);
    return { success: false, row: null, error: error?.message };
  }
}

/**
 * Returns safe public work-history rows for an expert (completed contracts + their reviews).
 */
export async function getExpertWorkHistory(expertProfileId) {
  try {
    const { data, error } = await supabase.rpc('get_expert_work_history', {
      p_expert_id: expertProfileId,
    });

    if (error) throw error;
    return { success: true, history: data || [] };
  } catch (error) {
    console.error('getExpertWorkHistory error:', error);
    return { success: false, history: [], error: error?.message };
  }
}
